import { useCallback, useEffect, useState } from 'react';
import {
  Star, Trash2, AlertTriangle, HardDrive, Undo2, Search as Lupe, RotateCcw,
  ArrowUp, ArrowDown,
} from 'lucide-react';
import { api } from '../lib/api';
import { Poster } from '../components/Poster';
import { bytes, relativ } from '../lib/format';

type Eintrag = {
  id: string; name: string; year: number | null; typ: string;
  rating: number | null; genres: string[]; overview: string | null;
  width: number | null; height: number | null; runtime_min: number | null;
  emby_path: string | null; host_path: string | null;
  on_disk: boolean; size_bytes: number | null; has_poster: boolean;
};
type Muell = { entry: string; name: string; deleted_at: string | null; size_bytes: number };
type Facetten = {
  genres: { name: string; count: number }[];
  resolutions: { name: string; count: number }[];
  missing: number; total: number; total_bytes: number;
};

const SORTIERUNGEN = [
  { wert: 'name', text: 'Titel' },
  { wert: 'added', text: 'Hinzugefügt' },
  { wert: 'year', text: 'Jahr' },
  { wert: 'rating', text: 'Bewertung' },
  { wert: 'size', text: 'Größe' },
  { wert: 'runtime', text: 'Laufzeit' },
] as const;

const PRO_SEITE = 48;

/** Auflösung als Kürzel — 1920×1080 sagt weniger als "1080p". */
function aufloesung(w: number | null, h: number | null): string | null {
  if (!w || !h) return null;
  const k = Math.max(w, h);
  if (k >= 3800) return '2160p';
  if (k >= 1900) return '1080p';
  if (k >= 1200) return '720p';
  return 'SD';
}

/**
 * Mediathek — was wirklich auf der Platte liegt.
 *
 * Metadaten und Poster kommen aus Emby; die Groesse und das Vorhandensein
 * werden hier auf der Platte nachgesehen. Emby kann Eintraege kennen,
 * deren Datei laengst weg ist — solche sind als "Datei fehlt" markiert
 * statt stillschweigend mitgezaehlt.
 */
export function Library() {
  const [items, setItems] = useState<Eintrag[] | null>(null);
  const [gesamt, setGesamt] = useState(0);
  const [seite, setSeite] = useState(0);
  const [typ, setTyp] = useState<'movie' | 'series'>('movie');
  const [suche, setSuche] = useState('');
  const [aktiveSuche, setAktiveSuche] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [detail, setDetail] = useState<Eintrag | null>(null);
  const [loeschen, setLoeschen] = useState<Eintrag | null>(null);
  const [muellOffen, setMuellOffen] = useState(false);
  const [sort, setSort] = useState<string>('name');
  const [desc, setDesc] = useState(false);
  const [genre, setGenre] = useState('');
  const [res, setRes] = useState('');
  const [nurFehlend, setNurFehlend] = useState(false);
  const [facetten, setFacetten] = useState<Facetten | null>(null);
  const [gesamtBytes, setGesamtBytes] = useState(0);

  const laden = useCallback(async () => {
    setItems(null);
    try {
      const p = new URLSearchParams({
        type: typ, limit: String(PRO_SEITE), offset: String(seite * PRO_SEITE),
        sort, desc: String(desc),
      });
      if (aktiveSuche) p.set('q', aktiveSuche);
      if (genre) p.set('genre', genre);
      if (res) p.set('resolution', res);
      if (nurFehlend) p.set('missing_only', 'true');
      const d = await api<{ items: Eintrag[]; total: number; total_bytes: number }>(`/library?${p}`);
      setItems(d.items);
      setGesamt(d.total);
      setGesamtBytes(d.total_bytes);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
      setItems([]);
    }
  }, [typ, seite, aktiveSuche, sort, desc, genre, res, nurFehlend]);

  useEffect(() => { void laden(); }, [laden]);

  useEffect(() => {
    let weg = false;
    api<Facetten>(`/library/facets?type=${typ}`)
      .then((d) => { if (!weg) setFacetten(d); })
      .catch(() => { if (!weg) setFacetten(null); });
    return () => { weg = true; };
  }, [typ]);


  return (
    <>
      <div className="kopf">
        <div>
          <h1>Mediathek</h1>
          <p>
            {gesamt.toLocaleString('de-DE')} {typ === 'movie' ? 'Filme' : 'Serien'}
            {gesamtBytes > 0 && <> · {bytes(gesamtBytes)}</>}
            {facetten && facetten.missing > 0 && (
              <> · <button className="leise" style={{ background: 'none', border: 'none', padding: 0, color: 'var(--warn)', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => { setNurFehlend(!nurFehlend); setSeite(0); }}>
                {facetten.missing} ohne Datei
              </button></>
            )}
          </p>
        </div>
        <button className="knopf" onClick={() => setMuellOffen(true)}>
          <Trash2 size={15} /> Papierkorb
        </button>
      </div>

      <div className="karte" style={{ padding: 12, marginBottom: 16 }}>
        <form className="leiste"
          onSubmit={(e) => { e.preventDefault(); setSeite(0); setAktiveSuche(suche.trim()); }}>
          {(['movie', 'series'] as const).map((t) => (
            <button key={t} type="button"
              className={`chip ${typ === t ? 'chip--akzent' : 'chip--neutral'}`}
              style={{ cursor: 'pointer', border: 'none', padding: '5px 13px' }}
              onClick={() => { setTyp(t); setSeite(0); setGenre(''); setRes(''); }}>
              {t === 'movie' ? 'Filme' : 'Serien'}
            </button>
          ))}

          <span className="leiste__trenner" />

          <input className="feld" style={{ flex: '1 1 190px', maxWidth: 300 }} value={suche}
            onChange={(e) => setSuche(e.target.value)} placeholder="Titel suchen …" />
          <button className="knopf knopf--klein" type="submit"><Lupe size={14} /> Suchen</button>

          <span className="leiste__trenner" />

          <span className="leiste__label">Sortieren</span>
          <select className="feld feld--klein" value={sort}
            onChange={(e) => { setSort(e.target.value); setSeite(0); }}>
            {SORTIERUNGEN.map((o) => <option key={o.wert} value={o.wert}>{o.text}</option>)}
          </select>
          {/* Richtung als eigener Knopf statt sechs weiterer Auswahleintraege
              ("Titel A–Z", "Titel Z–A", …) — das bleibt ueberschaubar. */}
          <button className="knopf knopf--klein knopf--nur-icon" type="button"
            title={desc ? 'Absteigend' : 'Aufsteigend'}
            onClick={() => { setDesc(!desc); setSeite(0); }}>
            {desc ? <ArrowDown size={15} /> : <ArrowUp size={15} />}
          </button>

          {facetten && facetten.genres.length > 0 && (
            <>
              <span className="leiste__trenner" />
              <select className="feld feld--klein" value={genre}
                onChange={(e) => { setGenre(e.target.value); setSeite(0); }}>
                <option value="">alle Genres</option>
                {facetten.genres.map((g) => (
                  <option key={g.name} value={g.name}>{g.name} ({g.count})</option>
                ))}
              </select>
            </>
          )}

          {facetten && facetten.resolutions.length > 0 && (
            <select className="feld feld--klein" value={res}
              onChange={(e) => { setRes(e.target.value); setSeite(0); }}>
              <option value="">alle Auflösungen</option>
              {facetten.resolutions.map((r) => (
                <option key={r.name} value={r.name}>{r.name} ({r.count})</option>
              ))}
            </select>
          )}

          {(aktiveSuche || genre || res || nurFehlend || sort !== 'name' || desc) && (
            <button className="knopf knopf--klein" type="button" onClick={() => {
              setSuche(''); setAktiveSuche(''); setGenre(''); setRes('');
              setNurFehlend(false); setSort('name'); setDesc(false); setSeite(0);
            }}>alles zurücksetzen</button>
          )}
        </form>

        {nurFehlend && (
          <div className="hinweis hinweis--warn klein" style={{ marginTop: 10 }}>
            Nur Einträge, die Emby kennt, deren Datei aber fehlt.
          </div>
        )}
      </div>

      {fehler && <div className="hinweis hinweis--schlecht" style={{ marginBottom: 14 }}>{fehler}</div>}

      {!items ? (
        <div className="laedt">Lade …</div>
      ) : items.length === 0 ? (
        <div className="karte karte__leer">Nichts gefunden.</div>
      ) : (
        <div className="poster-gitter">
          {items.map((i) => <Kachel key={i.id} e={i} aufKlick={() => setDetail(i)} />)}
        </div>
      )}

      {items && items.length > 0 && (
        <div className="reihe reihe--verteilt" style={{ marginTop: 18 }}>
          <span className="klein schwach">
            {seite * PRO_SEITE + 1}–{seite * PRO_SEITE + items.length} von {gesamt.toLocaleString('de-DE')}
          </span>
          <div className="reihe">
            <button className="knopf knopf--klein" disabled={seite === 0}
              onClick={() => setSeite((s) => s - 1)}>Zurück</button>
            <button className="knopf knopf--klein" disabled={(seite + 1) * PRO_SEITE >= gesamt}
              onClick={() => setSeite((s) => s + 1)}>Weiter</button>
          </div>
        </div>
      )}

      {detail && (
        <Detail e={detail} aufSchliessen={() => setDetail(null)}
          aufLoeschen={() => { setLoeschen(detail); setDetail(null); }} />
      )}

      {loeschen && (
        <LoeschDialog e={loeschen} aufSchliessen={() => setLoeschen(null)}
          aufFertig={async () => { setLoeschen(null); await laden(); }} />
      )}

      {muellOffen && <Papierkorb aufSchliessen={() => setMuellOffen(false)}
        aufAenderung={() => void laden()} />}
    </>
  );
}

function Kachel({ e, aufKlick }: { e: Eintrag; aufKlick: () => void }) {
  const res = aufloesung(e.width, e.height);
  return (
    <button className="poster" onClick={aufKlick} title={e.name}>
      <div className="poster__bild">
        {e.has_poster
          ? <Poster id={e.id} alt={e.name} />
          : <div className="poster__ersatz">{e.name.slice(0, 2).toUpperCase()}</div>}
        {!e.on_disk && <span className="poster__marke poster__marke--warn">Datei fehlt</span>}
        {res && e.on_disk && <span className="poster__marke">{res}</span>}
      </div>
      <div className="poster__titel">{e.name}</div>
      <div className="poster__zeile">
        {e.year ?? '—'}
        {e.size_bytes != null && <> · {bytes(e.size_bytes)}</>}
      </div>
    </button>
  );
}

function Detail({ e, aufSchliessen, aufLoeschen }: { e: Eintrag; aufSchliessen: () => void; aufLoeschen: () => void }) {
  const res = aufloesung(e.width, e.height);
  return (
    <div className="overlay" onClick={aufSchliessen}>
      <div className="karte dialog" style={{ maxWidth: 660 }} onClick={(ev) => ev.stopPropagation()}>
        <div className="karte__kopf">
          <span className="karte__titel">{e.name} {e.year && <span className="leise">({e.year})</span>}</span>
        </div>
        <div className="dialog__leib">
          <div className="reihe" style={{ alignItems: 'flex-start', gap: 16 }}>
            {e.has_poster && (
              <div className="detail__cover">
                <Poster id={e.id} alt={e.name} />
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="reihe" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {e.rating != null && <span className="chip chip--neutral"><Star size={11} /> {e.rating.toFixed(1)}</span>}
                {res && <span className="chip chip--neutral">{res}</span>}
                {e.runtime_min && <span className="chip chip--neutral">{e.runtime_min} Min.</span>}
                {e.size_bytes != null && <span className="chip chip--neutral">{bytes(e.size_bytes)}</span>}
                {e.on_disk
                  ? <span className="chip chip--gut">auf der Platte</span>
                  : <span className="chip chip--warn">Datei fehlt</span>}
              </div>
              {e.genres.length > 0 && <div className="klein leise" style={{ marginBottom: 8 }}>{e.genres.join(' · ')}</div>}
              {e.overview && <div className="klein leise" style={{ marginBottom: 10 }}>{e.overview}</div>}
              <div className="klein schwach mono" style={{ wordBreak: 'break-all' }}>{e.host_path ?? e.emby_path ?? '—'}</div>
            </div>
          </div>
        </div>
        <div className="dialog__fuss">
          <button className="knopf" onClick={aufSchliessen}>Schließen</button>
          {e.on_disk && (
            <button className="knopf knopf--gefahr" onClick={aufLoeschen}>
              <Trash2 size={15} /> Von der Platte entfernen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function LoeschDialog({ e, aufSchliessen, aufFertig }: { e: Eintrag; aufSchliessen: () => void; aufFertig: () => void | Promise<void> }) {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  return (
    <div className="overlay" onClick={aufSchliessen}>
      <div className="karte dialog" style={{ maxWidth: 470 }} onClick={(ev) => ev.stopPropagation()}>
        <div className="karte__kopf">
          <span className="karte__titel">
            <AlertTriangle size={16} style={{ verticalAlign: -3, marginRight: 6 }} />Von der Platte entfernen?
          </span>
        </div>
        <div className="dialog__leib stapel">
          {fehler && <div className="hinweis hinweis--schlecht">{fehler}</div>}
          <p style={{ margin: 0 }}>
            <strong>{e.name}</strong>{e.year && ` (${e.year})`} — {bytes(e.size_bytes)}
          </p>
          {/* Was wirklich passiert, muss dranstehen. "Loeschen" waere gelogen. */}
          <div className="hinweis">
            <Undo2 size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
            Wandert in den <strong>Papierkorb</strong> und lässt sich von dort
            zurückholen. Der Platz wird erst frei, wenn du den Papierkorb leerst.
          </div>
          <div className="klein schwach mono" style={{ wordBreak: 'break-all' }}>{e.host_path}</div>
        </div>
        <div className="dialog__fuss">
          <button className="knopf" onClick={aufSchliessen} disabled={laeuft}>Abbrechen</button>
          <button className="knopf knopf--gefahr" disabled={laeuft} onClick={async () => {
            setLaeuft(true);
            setFehler(null);
            try {
              await api(`/library/${e.id}`, { method: 'DELETE' });
              await aufFertig();
            } catch (err) {
              setFehler(err instanceof Error ? err.message : String(err));
            } finally {
              setLaeuft(false);
            }
          }}>
            <Trash2 size={15} /> {laeuft ? 'Verschiebt …' : 'In den Papierkorb'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Papierkorb({ aufSchliessen, aufAenderung }: { aufSchliessen: () => void; aufAenderung: () => void }) {
  const [items, setItems] = useState<Muell[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [leerenGefragt, setLeerenGefragt] = useState(false);

  const laden = useCallback(async () => {
    try {
      const d = await api<{ items: Muell[] }>('/library/trash');
      setItems(d.items);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
      setItems([]);
    }
  }, []);
  useEffect(() => { void laden(); }, [laden]);

  const belegt = (items ?? []).reduce((s, i) => s + i.size_bytes, 0);

  return (
    <div className="overlay" onClick={aufSchliessen}>
      <div className="karte dialog" style={{ maxWidth: 700 }} onClick={(e) => e.stopPropagation()}>
        <div className="karte__kopf">
          <span className="karte__titel"><Trash2 size={15} style={{ verticalAlign: -2, marginRight: 7 }} />Papierkorb</span>
          {items && items.length > 0 && <span className="chip chip--warn"><HardDrive size={11} /> {bytes(belegt)} belegt</span>}
        </div>

        {fehler && <div className="dialog__leib"><div className="hinweis hinweis--schlecht">{fehler}</div></div>}

        {!items ? <div className="laedt">Lade …</div>
          : items.length === 0 ? <div className="karte__leer">Papierkorb ist leer.</div>
          : (
            <table className="tabelle">
              <thead>
                <tr><th>Titel</th><th className="num" style={{ width: 90 }}>Größe</th>
                  <th className="num" style={{ width: 110 }}>Entfernt</th><th style={{ width: 130 }} /></tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.entry}>
                    <td className="klein">{m.name}</td>
                    <td className="num klein leise">{bytes(m.size_bytes)}</td>
                    <td className="num klein leise">{relativ(m.deleted_at)}</td>
                    <td>
                      <button className="knopf knopf--klein" disabled={laeuft} onClick={async () => {
                        setLaeuft(true);
                        try {
                          await api(`/library/trash/${encodeURIComponent(m.entry)}/restore`, { method: 'POST' });
                          await laden();
                          aufAenderung();
                        } catch (e) {
                          setFehler(e instanceof Error ? e.message : String(e));
                        } finally { setLaeuft(false); }
                      }}>
                        <RotateCcw size={13} /> Zurückholen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

        {leerenGefragt && (
          <div className="dialog__leib">
            <div className="hinweis hinweis--schlecht">
              <strong>Das ist endgültig.</strong> {items?.length} Einträge, {bytes(belegt)} —
              danach gibt es kein Zurückholen mehr.
            </div>
          </div>
        )}

        <div className="dialog__fuss">
          <button className="knopf" onClick={aufSchliessen}>Schließen</button>
          {items && items.length > 0 && (
            leerenGefragt ? (
              <>
                <button className="knopf" onClick={() => setLeerenGefragt(false)} disabled={laeuft}>Doch nicht</button>
                <button className="knopf knopf--gefahr" disabled={laeuft} onClick={async () => {
                  setLaeuft(true);
                  try {
                    await api('/library/trash/purge?older_than_days=0', { method: 'POST' });
                    setLeerenGefragt(false);
                    await laden();
                    aufAenderung();
                  } catch (e) {
                    setFehler(e instanceof Error ? e.message : String(e));
                  } finally { setLaeuft(false); }
                }}>
                  <Trash2 size={15} /> Endgültig löschen
                </button>
              </>
            ) : (
              <button className="knopf knopf--gefahr" onClick={() => setLeerenGefragt(true)}>
                Papierkorb leeren
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
